import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { View } from 'react-native';
import { CardView, rem } from 'design-system';
import { AppView } from 'design-system/AppView';
import Button from 'design-system/Button';
import { SettingsItemNavigate } from 'design-system/SettingsItem';
import Spacer from 'design-system/Spacer';
import StatusBar from 'design-system/StatusBar';
import { IconEmma, IconTwitter } from 'design-system/icons';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { useAppSelector } from 'store/hooks';
import { selectShowNewPhysicalAssets } from 'features/accounts/selectors';
import { Sign } from 'utils/types/navigation';
import { isYesterdayOrOlder } from 'utils/date/resetDay';
import { isWeb } from '../../../constants';
import { selectShouldShowCsvImports } from '../hooks/selectors';
import { getPropertyName, PHYSICAL_ASSET_TYPES } from '../constants';
const strings = {
    title: 'Add a logo',
    useXHandle: 'Use X handle',
    addEmoji: 'Add emoji',
    noThanks: 'No, thanks',
};
const DummyAccountLogoTypeScreen = ({ navigation, route }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
            title: strings.title,
        });
    }, [colors.text.primary]);
    const shouldShowCsvImports = useAppSelector(selectShouldShowCsvImports);
    const routeParams = useMemo(() => ({
        ...route.params,
        keyBack: route.params?.keyBack || route.key,
    }), [route.params, route?.key]);
    const onX = useCallback(() => {
        navigation.navigateDeprecated('DummyAccountLogo', {
            ...routeParams,
        });
    }, [routeParams]);
    const onEmoji = useCallback(() => {
        navigation.navigateDeprecated('AddAnEmoji', {
            ...routeParams,
        });
    }, [routeParams]);
    const showNewPhysicalAssets = useAppSelector(selectShowNewPhysicalAssets);
    const onAddValuation = useCallback(() => {
        navigation.navigateDeprecated('SelectDate', {
            longTitle: `When did you buy this ${getPropertyName(routeParams.type).toLowerCase()}?`,
            buttonText: 'Continue',
            onSubmitDate: (date) => {
                const isPastDate = isYesterdayOrOlder(date);
                const title = isPastDate ? 'What was the initial value?' : 'What is the current value?';
                navigation.navigateDeprecated('AddBalance', {
                    ...routeParams,
                    purchaseDate: date,
                    custom: {
                        title,
                        webSubtitle: title,
                        button: 'Continue',
                        initialSign: Sign.Positive,
                    },
                    ...(isPastDate && {
                        onContinue: (amount, currency) => {
                            navigation.navigateDeprecated('HasValuationChanged', {
                                ...routeParams,
                                purchaseDate: date,
                                initialValue: amount,
                                currency,
                            });
                        },
                    }),
                });
            },
        });
    }, [routeParams]);
    const onNothing = useCallback(() => {
        const type = route.params?.type;
        const isPhysicalAsset = PHYSICAL_ASSET_TYPES.has(type);
        if (route.params?.isChangingLogo) {
            navigation.navigateDeprecated('DummyAccountCreated', {
                ...routeParams,
            });
        }
        else if (isPhysicalAsset && showNewPhysicalAssets) {
            onAddValuation();
        }
        else if (shouldShowCsvImports && !isPhysicalAsset) {
            navigation.navigateDeprecated('DummyAccountCreationImport', {
                ...routeParams,
            });
        }
        else {
            navigation.navigateDeprecated('AddBalance', {
                ...routeParams,
            });
        }
    }, [
        route.params?.type,
        route.params?.isChangingLogo,
        showNewPhysicalAssets,
        shouldShowCsvImports,
        onAddValuation,
        routeParams,
    ]);
    return (<AppView type="secondary" isInTab={isWeb} avoidHeader>
      <StatusBar barStyle="dark-content" animated/>
      <View style={styles.container}>
        <CardView>
          <SettingsItemNavigate icon={<IconTwitter />} title={strings.useXHandle} onPress={onX} style={styles.containerPadding}/>
        </CardView>
        <CardView noMarginTop>
          <SettingsItemNavigate icon={<IconEmma />} title={strings.addEmoji} onPress={onEmoji} style={styles.containerPadding}/>
        </CardView>
        <Spacer flex/>
      </View>
      <Button brand title={strings.noThanks} onPress={onNothing} floating/>
    </AppView>);
};
const styleSet = createStyleSheets(() => ({
    container: {
        flex: 1,
    },
    centered: {
        alignSelf: 'center',
    },
    containerPadding: {
        ...(isWeb && {
            padding: rem(8),
        }),
    },
}));
export default DummyAccountLogoTypeScreen;
